import React, { useState } from 'react';
import { useToast } from '../../Toast';

interface AddPayerManuallyModalProps {
  onClose: () => void;
  onSave: (payer: {
    payerId: string;
    name: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
  }) => void;
}

const AddPayerManuallyModal: React.FC<AddPayerManuallyModalProps> = ({ onClose, onSave }) => {
  const toast = useToast();
  const [payerId, setPayerId] = useState('');
  const [payerName, setPayerName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');

  const handleSave = () => {
    // Validate required fields
    if (!payerId.trim()) {
      toast.warning('Please enter a Payer ID');
      return;
    }
    if (!payerName.trim()) {
      toast.warning('Please enter a Payer Name');
      return;
    }

    const fullAddress = [address, city, state, zipCode].filter(Boolean).join(', ');

    onSave({
      payerId: payerId.trim(),
      name: payerName.trim(),
      address: fullAddress,
      city: city.trim(),
      state: state.trim(),
      zipCode: zipCode.trim()
    });

    onClose();
  };

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-payer-title"
        aria-describedby="add-payer-description"
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #E5E7EB' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 id="add-payer-title" style={{ fontSize: '20px', fontWeight: 600, color: '#111827', margin: 0 }}>
              Add payer manually
            </h2>
            <button
              onClick={onClose}
              aria-label="Close add payer dialog"
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                color: '#6B7280',
                cursor: 'pointer',
                padding: '0',
                lineHeight: '1'
              }}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        </div>

        {/* Form Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          <p id="add-payer-description" style={{ fontSize: '14px', color: '#6B7280', marginBottom: '20px', marginTop: 0 }}>
            Enter payer information to add to your billing settings
          </p>
          <div style={{ marginBottom: '20px' }}>
            <label
              htmlFor="payerId"
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                color: '#374151',
                marginBottom: '6px'
              }}
            >
              Payer ID <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <input
              id="payerId"
              type="text"
              value={payerId}
              onChange={(e) => setPayerId(e.target.value)}
              placeholder="Enter payer ID"
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: '#FFFFFF',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#111827'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label
              htmlFor="payerName"
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                color: '#374151',
                marginBottom: '6px'
              }}
            >
              Payer name <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <input
              id="payerName"
              type="text"
              value={payerName}
              onChange={(e) => setPayerName(e.target.value)}
              placeholder="Enter payer name"
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: '#FFFFFF',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#111827'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label
              htmlFor="address"
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                color: '#374151',
                marginBottom: '6px'
              }}
            >
              Address
            </label>
            <input
              id="address"
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Street address"
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: '#FFFFFF',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#111827'
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 120px', gap: '12px', marginBottom: '20px' }}>
            <div>
              <label
                htmlFor="city"
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '6px'
                }}
              >
                City
              </label>
              <input
                id="city"
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#111827'
                }}
              />
            </div>

            <div>
              <label
                htmlFor="state"
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '6px'
                }}
              >
                State
              </label>
              <input
                id="state"
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value.toUpperCase())}
                placeholder="ST"
                maxLength={2}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#111827',
                  textTransform: 'uppercase'
                }}
              />
            </div>

            <div>
              <label
                htmlFor="zipCode"
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '6px'
                }}
              >
                ZIP code
              </label>
              <input
                id="zipCode"
                type="text"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                placeholder="12345"
                maxLength={10}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#111827'
                }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px'
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#FFFFFF',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              color: '#374151',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '10px 20px',
              backgroundColor: '#1E90FF',
              border: 'none',
              borderRadius: '6px',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Add payer
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPayerManuallyModal;
